import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getIngestionRunDetail,
  listIngestionRuns,
} from "../../../../api/adminIngestionLogsApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import AdminBtn from "../layout/AdminBtn";
import RequirePermission from "../../../../auth/RequirePermission";
import SourceHealthBadge from "../SourceHealthBadge";
import StatusBadge from "../../intelligence/StatusBadge";
import { formatDateTime } from "../users/adminUserFormatters";
import {
  buildIngestionIssueReport,
  buildIngestionIssuesDigest,
  copyTextToClipboard,
  isIngestionProblem,
  summarizeIngestionIssue,
} from "./ingestionIssueReport";
import {
  buildDataSourceDetailHref,
  navigateToDataSourceDetail,
} from "../../../../utils/dataSourceDetailRoutes";

function LogLevelBadge({ level }) {
  const tone =
    level === "error" ? "severe" : level === "warn" ? "medium" : level === "info" ? "neutral" : "low";

  return <StatusBadge label={level || "info"} tone={tone} />;
}

function RunStatusBadge({ status }) {
  const normalized = String(status || "").toLowerCase();
  const tone =
    normalized === "failed"
      ? "severe"
      : normalized === "running" || normalized === "queued"
        ? "medium"
        : normalized.includes("error")
          ? "medium"
          : "confirmed";

  return <StatusBadge label={status || "unknown"} tone={tone} />;
}

function MessageList({ items, emptyLabel }) {
  if (!items?.length) {
    return <p className="muted">{emptyLabel}</p>;
  }

  return (
    <ul className="admin-re-message-list">
      {items.map((item, index) => (
        <li key={`${item.message || item}-${index}`}>{item.message || String(item)}</li>
      ))}
    </ul>
  );
}

function CopyBtn({ label, copiedLabel = "Copied", onCopy, busy = false, copied = false, variant = "secondary" }) {
  return (
    <AdminBtn variant={variant} onClick={onCopy} disabled={busy}>
      {busy ? "Copying…" : copied ? copiedLabel : label}
    </AdminBtn>
  );
}

function currentPageUrl() {
  if (typeof window === "undefined") return "";
  return window.location.href;
}

async function reportForRun(run) {
  try {
    const data = await getIngestionRunDetail(run.runId);
    return buildIngestionIssueReport({
      run: data.run || run,
      logs: data.logs || [],
      logCounts: data.logCounts || {},
      pageUrl: currentPageUrl(),
    });
  } catch {
    return buildIngestionIssueReport({
      run,
      pageUrl: currentPageUrl(),
    });
  }
}

function RunDetailPanel({ detail, onClose, onCopyDiagnostic, copyState }) {
  if (!detail?.run) {
    return null;
  }

  const { run, logs = [], logCounts = {} } = detail;
  const sourceHref = run.sourceKey
    ? buildDataSourceDetailHref("real_estate", run.sourceKey)
    : null;

  return (
    <section className="admin-dash__card admin-dash__card--wide admin-ingestion-detail">
      <header className="admin-ingestion-detail__header">
        <div>
          <h2 className="admin-dash__card-title">
            {run.sourceName || run.sourceKey || "Ingestion run"}
          </h2>
          <span className="muted admin-re-source-code">{run.runId}</span>
        </div>
        <div className="admin-ingestion-detail__actions">
          {sourceHref ? (
            <AdminBtn
              variant="secondary"
              onClick={() => navigateToDataSourceDetail("real_estate", run.sourceKey)}
            >
              Open source
            </AdminBtn>
          ) : null}
          <CopyBtn
            label="Copy diagnostic"
            copiedLabel="Diagnostic copied"
            onCopy={onCopyDiagnostic}
            busy={copyState?.busy}
            copied={copyState?.copied}
            variant="primary"
          />
          <button type="button" className="admin-dashboard__action-btn" onClick={onClose}>
            Close
          </button>
        </div>
      </header>

      <p className="admin-ingestion-detail__guide">
        Copy the diagnostic and paste it into development chat or a ticket. It includes the
        production URL, run identity, errors, warnings, and step logs.
      </p>

      <div className="admin-ingestion-detail__meta-grid">
        <div>
          <span className="admin-dash__kpi-label">Status</span>
          <RunStatusBadge status={run.status} />
        </div>
        <div>
          <span className="admin-dash__kpi-label">Started</span>
          <p>{formatDateTime(run.startedAt)}</p>
        </div>
        <div>
          <span className="admin-dash__kpi-label">Finished</span>
          <p>{formatDateTime(run.finishedAt)}</p>
        </div>
        <div>
          <span className="admin-dash__kpi-label">Triggered by</span>
          <p>{run.triggeredBy || "—"}</p>
        </div>
      </div>

      {run.logSummary && <p className="muted">{run.logSummary}</p>}

      <div className="admin-ingestion-detail__meta-grid">
        <div>
          <span className="admin-dash__kpi-label">Found</span>
          <strong>{run.recordsFound ?? 0}</strong>
        </div>
        <div>
          <span className="admin-dash__kpi-label">Created</span>
          <strong>{run.recordsCreated ?? 0}</strong>
        </div>
        <div>
          <span className="admin-dash__kpi-label">Updated</span>
          <strong>{run.recordsUpdated ?? 0}</strong>
        </div>
        <div>
          <span className="admin-dash__kpi-label">Failed rows</span>
          <strong>{run.recordsFailed ?? 0}</strong>
        </div>
        <div>
          <span className="admin-dash__kpi-label">Warnings</span>
          <strong>{run.warningCount ?? 0}</strong>
        </div>
        <div>
          <span className="admin-dash__kpi-label">Step logs</span>
          <strong>
            {logCounts.total ?? 0} ({logCounts.errors ?? 0} err, {logCounts.warnings ?? 0} warn)
          </strong>
        </div>
      </div>

      {run.errorMessage ? (
        <p className="admin-data-health-error">{run.errorMessage}</p>
      ) : null}

      {run.stackTrace ? (
        <section className="admin-ingestion-detail__section">
          <h3>Stack trace</h3>
          <pre className="admin-ingestion-detail__stack">{run.stackTrace}</pre>
        </section>
      ) : null}

      <section className="admin-ingestion-detail__section">
        <h3>Warnings</h3>
        <MessageList items={run.warnings} emptyLabel="No warnings recorded." />
      </section>

      <section className="admin-ingestion-detail__section">
        <h3>Errors</h3>
        <MessageList items={run.errors} emptyLabel="No errors recorded." />
      </section>

      <section className="admin-ingestion-detail__section">
        <h3>Step log timeline</h3>
        {!logs.length ? (
          <p className="muted">No step logs persisted for this run yet.</p>
        ) : (
          <AdminTableContainer>
            <table className="admin-data-health-table admin-ingestion-log-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Step</th>
                  <th>Level</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((entry) => (
                  <tr key={entry.id}>
                    <td>{formatDateTime(entry.createdAt)}</td>
                    <td>
                      <code>{entry.step}</code>
                    </td>
                    <td>
                      <LogLevelBadge level={entry.level} />
                    </td>
                    <td>
                      <div>{entry.message}</div>
                      {entry.context && Object.keys(entry.context).length ? (
                        <pre className="admin-audit-metadata__json">
                          {JSON.stringify(entry.context, null, 2)}
                        </pre>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </AdminTableContainer>
        )}
      </section>
    </section>
  );
}

function AdminIngestionLogsPageContent({ onClose, onOpenAuditLog }) {
  const [runs, setRuns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [sourceKeyFilter, setSourceKeyFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [problemsOnly, setProblemsOnly] = useState(true);
  const [selectedRunId, setSelectedRunId] = useState(null);
  const [runDetail, setRunDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [copyKey, setCopyKey] = useState("");
  const [copyBusy, setCopyBusy] = useState(false);
  const [copyMessage, setCopyMessage] = useState("");
  const [copyTone, setCopyTone] = useState("success");

  const loadRuns = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const data = await listIngestionRuns({
        limit: 80,
        sourceKey: sourceKeyFilter.trim() || undefined,
        runStatus: statusFilter || undefined,
      });
      setRuns(data.runs || []);
    } catch (error) {
      setRuns([]);
      setErrorMessage(error.message || "Unable to load ingestion runs.");
    } finally {
      setIsLoading(false);
    }
  }, [sourceKeyFilter, statusFilter]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    if (!selectedRunId) {
      setRunDetail(null);
      return undefined;
    }

    let cancelled = false;

    const loadDetail = async () => {
      try {
        setDetailLoading(true);
        const data = await getIngestionRunDetail(selectedRunId);
        if (!cancelled) {
          setRunDetail(data);
        }
      } catch (error) {
        if (!cancelled) {
          setRunDetail(null);
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    };

    loadDetail();

    return () => {
      cancelled = true;
    };
  }, [selectedRunId]);

  useEffect(() => {
    if (!copyMessage) return undefined;
    const timer = setTimeout(() => setCopyMessage(""), 4000);
    return () => clearTimeout(timer);
  }, [copyMessage]);

  const problemRuns = useMemo(() => runs.filter(isIngestionProblem), [runs]);
  const visibleRuns = problemsOnly ? problemRuns : runs;

  const kpis = useMemo(() => {
    const total = runs.length;
    const completed = runs.filter((r) => r.runStatus === "completed").length;
    const failed = runs.filter((r) => r.status === "FAILED" || r.runStatus === "failed").length;
    const withErrors = runs.filter((r) => r.runStatus === "completed_with_errors").length;
    const totalWarnings = runs.reduce((sum, r) => sum + (r.warningCount ?? 0), 0);
    const running = runs.filter(
      (r) => r.runStatus === "running" || r.runStatus === "queued"
    ).length;

    return { total, completed, failed, withErrors, totalWarnings, running, problems: problemRuns.length };
  }, [runs, problemRuns]);

  const markCopied = (key, message) => {
    setCopyKey(key);
    setCopyTone("success");
    setCopyMessage(message);
  };

  const handleCopyText = async (key, text, successMessage) => {
    setCopyBusy(true);
    setCopyKey(key);
    try {
      await copyTextToClipboard(text);
      markCopied(key, successMessage);
    } catch (error) {
      setCopyTone("error");
      setCopyMessage(error.message || "Unable to copy.");
    } finally {
      setCopyBusy(false);
    }
  };

  const handleCopyRun = async (run) => {
    setCopyBusy(true);
    setCopyKey(run.runId);
    try {
      const report = await reportForRun(run);
      await copyTextToClipboard(report);
      markCopied(run.runId, `Copied diagnostic for ${run.sourceName || run.sourceKey}.`);
    } catch (error) {
      setCopyTone("error");
      setCopyMessage(error.message || "Unable to copy this issue.");
    } finally {
      setCopyBusy(false);
    }
  };

  const handleCopyProblems = async () => {
    const targets = problemRuns.slice(0, 20);
    if (!targets.length) {
      setCopyTone("error");
      setCopyMessage("No failing runs to copy.");
      return;
    }

    setCopyBusy(true);
    setCopyKey("problems");
    try {
      const reports = await Promise.all(targets.map((run) => reportForRun(run)));
      const digest = buildIngestionIssuesDigest({
        reports,
        pageUrl: currentPageUrl(),
      });
      await copyTextToClipboard(digest);
      markCopied(
        "problems",
        `Copied ${reports.length} production issue${reports.length === 1 ? "" : "s"}.`
      );
    } catch (error) {
      setCopyTone("error");
      setCopyMessage(error.message || "Unable to copy failing runs.");
    } finally {
      setCopyBusy(false);
    }
  };

  const handleCopyDiagnostic = async () => {
    if (!runDetail?.run) return;
    await handleCopyText(
      "detail",
      buildIngestionIssueReport({
        run: runDetail.run,
        logs: runDetail.logs || [],
        logCounts: runDetail.logCounts || {},
        pageUrl: currentPageUrl(),
      }),
      "Copied full run diagnostic."
    );
  };

  return (
    <div className="admin-dashboard admin-ingestion-dash">
      {copyMessage ? (
        <div className={`admin-re-toast admin-re-toast--${copyTone}`} role="status" aria-live="polite">
          {copyMessage}
        </div>
      ) : null}

      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Ingestion Logs</h1>
          <p className="admin-dashboard__subtitle">
            Production collector runs, errors, and step logs. Copy an issue and paste it into
            development to reproduce and fix it.
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <CopyBtn
            label={`Copy failing issues (${kpis.problems})`}
            copiedLabel="Failing issues copied"
            onCopy={handleCopyProblems}
            busy={copyBusy && copyKey === "problems"}
            copied={copyKey === "problems" && Boolean(copyMessage) && copyTone === "success"}
            variant="primary"
          />
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadRuns}
            disabled={isLoading}
          >
            Refresh
          </button>
          {onOpenAuditLog && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onOpenAuditLog}
            >
              Audit Log
            </button>
          )}
          {onClose && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onClose}
            >
              ← Dashboard
            </button>
          )}
        </div>
      </header>

      <section className="admin-dashboard__kpis">
        <div className="admin-dash__kpi-card">
          <span className="admin-dash__kpi-label">Loaded runs</span>
          <span className="admin-dash__kpi-value">{kpis.total}</span>
        </div>
        <div className="admin-dash__kpi-card">
          <span className="admin-dash__kpi-label">Completed</span>
          <span className="admin-dash__kpi-value admin-dash__kpi-value--green">
            {kpis.completed}
          </span>
        </div>
        <div className="admin-dash__kpi-card">
          <span className="admin-dash__kpi-label">Problems</span>
          <span
            className={`admin-dash__kpi-value${kpis.problems > 0 ? " admin-dash__kpi-value--red" : ""}`}
          >
            {kpis.problems}
          </span>
        </div>
        <div className="admin-dash__kpi-card">
          <span className="admin-dash__kpi-label">Failed</span>
          <span
            className={`admin-dash__kpi-value${kpis.failed > 0 ? " admin-dash__kpi-value--red" : ""}`}
          >
            {kpis.failed}
          </span>
        </div>
        <div className="admin-dash__kpi-card">
          <span className="admin-dash__kpi-label">With Errors</span>
          <span
            className={`admin-dash__kpi-value${kpis.withErrors > 0 ? " admin-dash__kpi-value--amber" : ""}`}
          >
            {kpis.withErrors}
          </span>
        </div>
        <div className="admin-dash__kpi-card">
          <span className="admin-dash__kpi-label">Warnings</span>
          <span
            className={`admin-dash__kpi-value${kpis.totalWarnings > 0 ? " admin-dash__kpi-value--amber" : ""}`}
          >
            {kpis.totalWarnings}
          </span>
        </div>
        {kpis.running > 0 && (
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Running</span>
            <span className="admin-dash__kpi-value">
              <span className="admin-dash__pulse" /> {kpis.running}
            </span>
          </div>
        )}
      </section>

      <section className="admin-dash__card admin-dash__card--wide admin-ingestion-dash__table-card">
        <div className="admin-ingestion-dash__toolbar">
          <div className="admin-ingestion-dash__filters">
            <input
              type="search"
              className="admin-source-controls-dash__search"
              placeholder="Source key…"
              value={sourceKeyFilter}
              onChange={(event) => setSourceKeyFilter(event.target.value)}
            />
            <select
              className="admin-source-controls-dash__select"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="">All statuses</option>
              <option value="completed">completed</option>
              <option value="completed_with_errors">completed_with_errors</option>
              <option value="failed">failed</option>
              <option value="running">running</option>
              <option value="queued">queued</option>
            </select>
            <label className="admin-ingestion-dash__toggle">
              <input
                type="checkbox"
                checked={problemsOnly}
                onChange={(event) => setProblemsOnly(event.target.checked)}
              />
              Problems only
            </label>
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={loadRuns}
              disabled={isLoading}
            >
              Apply
            </button>
          </div>
        </div>

        {isLoading ? <AdminStatePanel variant="loading" message="Loading ingestion runs…" /> : null}

        {!isLoading && errorMessage ? (
          <AdminStatePanel variant="error" message={errorMessage} onRetry={loadRuns} />
        ) : null}

        {!isLoading && !errorMessage ? (
          <AdminTableContainer>
            <table className="admin-data-health-table">
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Status</th>
                  <th>Issue</th>
                  <th>Started</th>
                  <th>Records</th>
                  <th>Logs</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleRuns.map((run) => {
                  const issue = summarizeIngestionIssue(run);
                  const isProblem = isIngestionProblem(run);
                  return (
                    <tr
                      key={run.runId}
                      className={`${selectedRunId === run.runId ? "is-selected" : ""}${
                        isProblem ? " admin-ingestion-row--problem" : ""
                      }`}
                    >
                      <td>
                        {run.sourceKey ? (
                          <a
                            className="admin-ds-source-name-link"
                            href={buildDataSourceDetailHref("real_estate", run.sourceKey)}
                            onClick={(event) => {
                              event.preventDefault();
                              navigateToDataSourceDetail("real_estate", run.sourceKey);
                            }}
                          >
                            {run.sourceName || run.sourceKey}
                          </a>
                        ) : (
                          <strong>{run.sourceName || run.sourceKey}</strong>
                        )}
                        <div className="muted admin-re-source-code">{run.sourceKey}</div>
                      </td>
                      <td>
                        <SourceHealthBadge
                          status={
                            run.status === "FAILED" || run.runStatus === "failed"
                              ? "failed"
                              : isProblem
                                ? "warning"
                                : run.warningCount > 0
                                  ? "warning"
                                  : "healthy"
                          }
                        />
                        <div className="muted">{run.runStatus}</div>
                      </td>
                      <td className="admin-ingestion-issue-cell">
                        {issue ? (
                          <div className="admin-re-run-error" title={issue}>
                            {issue.length > 140 ? `${issue.slice(0, 140)}…` : issue}
                          </div>
                        ) : (
                          <span className="muted">No error recorded</span>
                        )}
                      </td>
                      <td>{formatDateTime(run.startedAt)}</td>
                      <td>
                        {run.recordsFound} found · {run.recordsCreated} created · {run.recordsUpdated}{" "}
                        updated
                        {run.recordsFailed > 0 ? ` · ${run.recordsFailed} failed` : ""}
                      </td>
                      <td>
                        {run.logEventCount ?? 0}
                        {(run.logErrorCount ?? 0) > 0 ? ` (${run.logErrorCount} err)` : ""}
                      </td>
                      <td className="admin-ingestion-actions-cell">
                        <AdminBtn
                          variant="secondary"
                          onClick={() => setSelectedRunId(run.runId)}
                        >
                          View
                        </AdminBtn>
                        {isProblem ? (
                          <CopyBtn
                            label="Copy issue"
                            copiedLabel="Copied"
                            onCopy={() => handleCopyRun(run)}
                            busy={copyBusy && copyKey === run.runId}
                            copied={copyKey === run.runId && copyTone === "success"}
                          />
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
                {!visibleRuns.length ? (
                  <tr>
                    <td colSpan={7} className="muted">
                      {problemsOnly
                        ? "No failing ingestion runs in the current results. Turn off Problems only to see every run."
                        : "No pipeline ingestion runs match the current filters."}
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </AdminTableContainer>
        ) : null}
      </section>

      {detailLoading ? <AdminStatePanel variant="loading" message="Loading run detail…" /> : null}

      {!detailLoading && runDetail ? (
        <RunDetailPanel
          detail={runDetail}
          copyState={{ busy: copyBusy && copyKey === "detail", copied: copyKey === "detail" && copyTone === "success" }}
          onCopyDiagnostic={handleCopyDiagnostic}
          onClose={() => {
            setSelectedRunId(null);
            setRunDetail(null);
          }}
        />
      ) : null}
    </div>
  );
}

export default function AdminIngestionLogsPage(props) {
  return (
    <RequirePermission permission="real_estate.sources.view">
      <AdminIngestionLogsPageContent {...props} />
    </RequirePermission>
  );
}
